import { Card, Group, Stack, Text } from '@mantine/core';
import { InfoNote } from './info-note';
import classes from './stat-tile.module.css';

interface StatTileProps {
  label: string;
  /** The figure itself. A string so an em dash can stand in for "no answer yet". */
  value: string;
  /** One line directly under the value — a rate, a comparison, a denominator. */
  secondary?: string;
  /** The caveat. Behind an info icon rather than printed, so it stops competing. */
  note?: string;
  /** Colour of the value. Defaults to the body colour; use sparingly. */
  tone?: 'brand' | 'teal' | 'magenta' | 'red' | 'dimmed';
  /**
   * `card` stands alone; `plain` is borderless for use inside a panel.
   *
   * A tile is a Card, so a row of them inside another Card produced two
   * bordered surfaces at the same radius and elevation — which reads as mush.
   */
  variant?: 'card' | 'plain';
  /** Display size, for the one figure on a screen that is the headline. */
  size?: 'md' | 'lg';
}

/** A single figure with its label and caveats. */
export function StatTile({
  label,
  value,
  secondary,
  note,
  tone,
  variant = 'card',
  size = 'md',
}: StatTileProps) {
  const body = (
    <Stack gap={4}>
      <Group gap={2} wrap="nowrap" className={classes.labelRow}>
        <Text size="xs" fw={600} c="dimmed" className={classes.label}>
          {label}
        </Text>
        {note && <InfoNote label={`About ${label.toLowerCase()}`}>{note}</InfoNote>}
      </Group>
      <Text
        className={size === 'lg' ? classes.valueLarge : classes.value}
        c={tone}
      >
        {value}
      </Text>
      {secondary && (
        <Text size="sm" c="dimmed" lh={1.35}>
          {secondary}
        </Text>
      )}
    </Stack>
  );

  if (variant === 'plain') return body;

  return <Card>{body}</Card>;
}
